import { useTranslation } from "react-i18next";

import { IconCalendar, IconTrash01 } from "@pentabd/icons";
import { Badge, Button, InputDate, Modal, Text } from "@pentabd/ui";

import { SectionTitle } from "@components";
import { extraResponsibilityValues } from "@containers/HRM/EmployeeList/components/EmployeeTable/const";

import { useAuth } from "@hooks/useAuth";

import { transformToReadableString } from "@utils/transformText";
import { Controller, FormProvider, useForm } from "react-hook-form";

interface RoleTypes {
  id?: string | number;
  tempAccMasterId?: string | number;
  email: string;
  position: string;
  type: string;
}

interface ExtraRoleModalProps {
  isModalOpen: boolean;
  onCloseAction: () => void;
  roles: RoleTypes[];
  pendingRoles?: RoleTypes[];
  deleteAction?: (arg: number, serviceEndDate: string) => void;
}

export const ExtraRoleModal = ({
  isModalOpen,
  onCloseAction,
  roles,
  pendingRoles,
  deleteAction,
}: ExtraRoleModalProps) => {
  const { logout } = useAuth();
  const { t } = useTranslation();

  const methods = useForm();
  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitting },
    setValue,
  } = methods;

  const onsubmit = (data: any) => {
    const mail = data[`selectedMail`];
    const masterId = data[`selectedMasterId`];
    const serviceEndDate = data[`serviceEndDate-${mail}`];

    if (serviceEndDate) {
      if (deleteAction) {
        deleteAction(Number(masterId), serviceEndDate as string);
      } else {
        logout({
          redirectUri: `${process.env.REACT_APP_KEYCLOAK_BASE_URL}${
            process.env.REACT_APP_KEYCLOAK_PORT
          }/realms/${
            process.env.REACT_APP_KEYCLOAK_REALM
          }/protocol/openid-connect/auth?login_hint=${mail}&client_id=${
            process.env.REACT_APP_KEYCLOAK_CLIENT_ID
          }&redirect_uri=${
            window.location.host === "localhost:3000"
              ? process.env.REACT_APP_KEYCLOAK_LOGOUT_REDIRECT_URI_LOCAL
              : process.env.REACT_APP_KEYCLOAK_LOGOUT_REDIRECT_URI
          }&state=bc37fc3d-ca0d-467b-869a-bc576d3f16fe&response_mode=fragment&response_type=code&scope=openid&nonce=11992133-9657-4062-99c9-e330fc534eb1`,
        });
      }
    }
  };
  const Roles = ({ data, label }: { data: any; label: string }) => (
    <div>
      <SectionTitle label={label} />
      <div className="bg-light rounded p-5 d-flex gap-6 flex-wrap justify-content-between align-items-baseline">
        {data?.length > 0 ? (
          data?.map(
            ({ id, tempAccMasterId, email, position, type }: RoleTypes) => {
              return (
                <FormProvider {...methods} key={email}>
                  <form
                    className="d-flex gap-4 align-items-baseline"
                    onSubmit={handleSubmit(onsubmit)}
                    key={email}
                  >
                    <Controller
                      control={methods.control}
                      name={`serviceEndDate-${email}`}
                      render={({ field }) => {
                        return (
                          <InputDate
                            {...register(field.name, {
                              required: "Service end date is required",
                            })}
                            name={`serviceEndDate-${email}`}
                            prefix={<IconCalendar size="20" fill="subtitle2" />}
                            onSelectDate={(date) => {
                              field.onChange(date);
                              setValue(`serviceEndDate-${email}`, date);
                              setValue(`selectedMasterId`, tempAccMasterId);
                              setValue(`selectedMail`, email);
                            }}
                            placeholder={t(
                              "EMPLOYEES.SERVICE_END_DATE.PLACEHOLDER",
                            )}
                            error={
                              errors?.[`serviceEndDate-${email}`]
                                ?.message as string
                            }
                          />
                        );
                      }}
                    />
                    <Button
                      key={email}
                      fill={id === "parent_account" ? "fill" : "outline"}
                      size="xs"
                      type={deleteAction ? "danger" : "primary"}
                      htmlType="submit"
                      loading={isSubmitting}
                    >
                      {deleteAction && <IconTrash01 size="16" fill="danger" />}
                      {position}
                      <Badge
                        label={transformToReadableString(type)}
                        type={
                          type === extraResponsibilityValues.ATTACHMENT
                            ? "success"
                            : "warning"
                        }
                        className="w-max"
                        size="sm"
                      />
                    </Button>
                  </form>
                </FormProvider>
              );
            },
          )
        ) : (
          <Text color="subtitle1">{t("EMPLOYEES.NO_ROLE_AVAILABLE")}</Text>
        )}
      </div>
    </div>
  );

  return (
    <Modal
      isOpen={isModalOpen}
      onClose={() => onCloseAction()}
      overlay
      closeAble
    >
      <div className="px-10 pb-10 modal_width d-flex flex-column gap-10">
        <Roles label="EMPLOYEES.SWITCH_ROLE_MODAL_HEADING" data={roles} />
        {pendingRoles && (
          <Roles
            label="EMPLOYEES.SWITCH_ROLE_MODAL_PENDING"
            data={pendingRoles}
          />
        )}
      </div>
    </Modal>
  );
};
